import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

export interface CreamPercentageData {
  cycleNum: number;
  formData: {
    product: string;
    machineNo: string;
    line: string;
    standardCreamPercentage: string;
    executiveName?: string;
  };
  weightData: {
    sandwichWeights: string[];
    shellWeights: string[];
  };
  qualityTourId: string;
  userName: string | null;
  shiftValue: string;
  timestamp: string;
}

interface CreamPercentageState {
  cycleData: { [key: number]: CreamPercentageData[] }; // Multiple products per cycle
  completedCycles: number[];
  currentCycle: number;
  isOffline: boolean;
  pendingSync: CreamPercentageData[];
}

const initialState: CreamPercentageState = {
  cycleData: {},
  completedCycles: [],
  currentCycle: 1,
  isOffline: false,
  pendingSync: []
};

const creamPercentageSlice = createSlice({
  name: 'creamPercentage',
  initialState,
  reducers: {
    saveCycleData: (state, action: PayloadAction<CreamPercentageData & { forceOffline?: boolean }>) => {
      const { forceOffline, ...rest } = action.payload;
      const payload = { ...rest, timestamp: new Date().toISOString() };
      const { cycleNum } = payload;
      
      console.log(`=== REDUX CREAM SLICE: SAVING PRODUCT FOR CYCLE ${cycleNum} ===`);
      console.log('Action payload:', action.payload);
      console.log('forceOffline:', forceOffline);
      console.log('state.isOffline:', state.isOffline);
      console.log('Payload to save:', payload);
      console.log('Product name:', payload.formData?.product);
      
      if (!state.cycleData[cycleNum]) {
        state.cycleData[cycleNum] = [];
      }
      state.cycleData[cycleNum].push(payload);
      
      if (!state.completedCycles.includes(cycleNum)) {
        state.completedCycles.push(cycleNum);
      }
      
      // Do NOT advance currentCycle - user must click "Complete Cycle"
      console.log(`Cycle ${cycleNum} now has ${state.cycleData[cycleNum].length} product(s)`);
      
      // Add to pendingSync if offline (either slice state or forced by component)
      const shouldAddToPendingSync = state.isOffline || forceOffline;
      console.log('Should add to pendingSync:', shouldAddToPendingSync, '(isOffline:', state.isOffline, ', forceOffline:', forceOffline, ')');
      
      if (shouldAddToPendingSync) {
        state.pendingSync.push(payload);
        console.log(`=== ADDED TO PENDING SYNC ===`);
        console.log(`Total items in pendingSync: ${state.pendingSync.length}`);
        console.log('Current pendingSync items:', state.pendingSync.map(p => ({ cycleNum: p.cycleNum, product: p.formData?.product, timestamp: p.timestamp })));
      } else {
        console.log('NOT adding to pendingSync - not in offline mode');
      }
    },
    
    completeCycle: (state, action: PayloadAction<number>) => {
      const cycleNum = action.payload;
      state.currentCycle = Math.min(cycleNum + 1, 9);
      console.log(`Cycle ${cycleNum} completed. Moving to cycle ${state.currentCycle}`);
    },
    
    setOfflineMode: (state, action: PayloadAction<boolean>) => {
      state.isOffline = action.payload;
    },
    
    loadOfflineData: (state, action: PayloadAction<{
      cycleData: { [key: number]: CreamPercentageData | CreamPercentageData[] };
      completedCycles: number[];
      currentCycle: number;
    }>) => {
      const raw = action.payload.cycleData;
      state.cycleData = {};
      Object.entries(raw || {}).forEach(([k, v]) => {
        const cycleNum = parseInt(k);
        state.cycleData[cycleNum] = Array.isArray(v) ? v : [v];
      });
      state.completedCycles = action.payload.completedCycles;
      state.currentCycle = action.payload.currentCycle;
    },
    
    clearPendingSync: (state) => {
      state.pendingSync = [];
    },
    
    removePendingSyncItem: (state, action: PayloadAction<number>) => {
      const indexToRemove = action.payload;
      if (typeof indexToRemove === 'number' && indexToRemove >= 0 && indexToRemove < state.pendingSync.length) {
        state.pendingSync.splice(indexToRemove, 1);
      }
    },
    
    resetCreamPercentage: (state) => {
      state.cycleData = {};
      state.completedCycles = [];
      state.currentCycle = 1;
      state.pendingSync = [];
      state.isOffline = false;
    }
  }
});

export const {
  saveCycleData,
  completeCycle,
  setOfflineMode,
  loadOfflineData,
  clearPendingSync,
  removePendingSyncItem,
  resetCreamPercentage
} = creamPercentageSlice.actions;

export default creamPercentageSlice.reducer;
